import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  api,
  listMemberships,
  previewSlotTerms,
  type Membership,
} from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';

function slotTypeLabel(t: 'paid' | 'cd' | 'half_cd'): string {
  return t === 'cd' ? 'CD (Commission Deductible)' : t === 'half_cd' ? 'Half-CD' : 'Paid';
}

/** Display-only formatting of a 4dp debt string (e.g. "2500.0000" → "2,500.00"). */
function formatDebt(amount: string): string {
  return Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export type CreateSlotDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaults?: {
    sponsorCode?: string;
    sponsorLocked?: boolean;
    /**
     * Create the slot AND place it at this exact L/R seat atomically. Used when
     * the admin clicks an empty seat in the binary tree; the request body
     * carries the placement payload.
     */
    placement?: { parentSlotId: string; position: 'L' | 'R' };
  };
  onSuccess?: () => void;
};

export function CreateSlotDialog({
  open,
  onOpenChange,
  defaults,
  onSuccess,
}: CreateSlotDialogProps) {
  const qc = useQueryClient();
  const currentUserEmail = useAuthStore((s) => s.user?.email ?? '');
  const [ownerEmail, setOwnerEmail] = useState(currentUserEmail);
  const [sponsorCode, setSponsorCode] = useState(defaults?.sponsorCode ?? '');
  const [membershipCode, setMembershipCode] = useState('');
  const [isCompany, setIsCompany] = useState(false);
  const [kitChoice, setKitChoice] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setOwnerEmail(currentUserEmail);
      setSponsorCode(defaults?.sponsorCode ?? '');
    }
  }, [open, currentUserEmail, defaults?.sponsorCode]);

  const membershipsQ = useQuery({
    queryKey: ['memberships'],
    queryFn: listMemberships,
    enabled: open,
  });

  useEffect(() => {
    if (!membershipCode && membershipsQ.data?.length) {
      setMembershipCode(membershipsQ.data[0]!.code);
    }
  }, [membershipsQ.data, membershipCode]);

  // Slot type + opening debt are derived server-side from the membership's kit
  // product(s) — never free-typed. The kit picker only appears when the tier
  // has several kits with conflicting terms.
  const termsQ = useQuery({
    queryKey: ['slot-terms', membershipCode],
    queryFn: () => previewSlotTerms(membershipCode),
    enabled: open && !!membershipCode,
  });
  const requiresKitChoice = termsQ.data?.requiresKitChoice ?? false;
  const chosenKit = termsQ.data?.kits.find((k) => k.kitProductId === kitChoice) ?? null;
  const effectiveTerms = requiresKitChoice ? chosenKit : (termsQ.data?.derived ?? null);

  // Reset any prior kit pick when the membership (and thus its kit set) changes.
  useEffect(() => {
    setKitChoice('');
  }, [membershipCode]);

  function reset() {
    setOwnerEmail(currentUserEmail);
    setSponsorCode('');
    setMembershipCode('');
    setIsCompany(false);
    setKitChoice('');
    setError(null);
  }

  const mut = useMutation({
    mutationFn: async () =>
      (
        await api.post('/admin/slots', {
          ownerEmail,
          sponsorCode,
          membershipCode,
          isCompany,
          ...(requiresKitChoice && kitChoice ? { kitProductId: kitChoice } : {}),
          ...(defaults?.placement ? { placement: defaults.placement } : {}),
        })
      ).data,
    meta: { successMessage: 'Slot created.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slots'] });
      onSuccess?.();
      reset();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string } } };
      setError((e.response?.data?.error ?? 'create_failed').replace(/_/g, ' '));
    },
  });

  const canSubmit =
    !!ownerEmail &&
    !!sponsorCode &&
    !!membershipCode &&
    (!requiresKitChoice || !!kitChoice);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) reset(); onOpenChange(next); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create slot</DialogTitle>
          <DialogDescription>
            Creates an active slot for the chosen owner under the given sponsor and tier.
            Admin-created slots are activated immediately — there is no pending state.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Owner email</label>
            <Input
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="member@example.com"
              required
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              Sponsor slot code
            </label>
            <Input
              value={sponsorCode}
              onChange={(e) => setSponsorCode(e.target.value.toUpperCase())}
              placeholder="e.g. SK8N3PQRX7"
              required
              readOnly={defaults?.sponsorLocked}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {defaults?.sponsorLocked
                ? 'Locked to the slot you clicked.'
                : "Slot code of the sponsoring slot. Referral codes are not accepted; they're the replicated-link entry-point only."}
            </p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Membership</label>
            <Select value={membershipCode} onValueChange={setMembershipCode}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(membershipsQ.data ?? []).map((m: Membership) => (
                  <SelectItem key={m.id} value={m.code}>
                    {m.name} ({m.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Slot type</label>
            {termsQ.isLoading ? (
              <p className="mt-1 text-sm text-muted-foreground">
                Deriving from the membership&apos;s kit…
              </p>
            ) : requiresKitChoice ? (
              <div className="space-y-1">
                <Select value={kitChoice} onValueChange={setKitChoice}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Choose a kit…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(termsQ.data?.kits ?? []).map((k) => (
                      <SelectItem key={k.kitProductId} value={k.kitProductId}>
                        {k.name} — {slotTypeLabel(k.slotType)}
                        {k.debtAmount ? ` (debt ${formatDebt(k.debtAmount)})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  This membership has multiple kits with different terms — pick which one this
                  slot is created from.
                </p>
              </div>
            ) : (
              <p className="mt-1 text-sm">
                {slotTypeLabel(effectiveTerms?.slotType ?? 'paid')}
                {effectiveTerms?.debtAmount
                  ? ` — opening debt ${formatDebt(effectiveTerms.debtAmount)}`
                  : ''}
              </p>
            )}
            {effectiveTerms && effectiveTerms.slotType !== 'paid' && (
              <p className="mt-1 text-[10px] text-muted-foreground">
                Derived from the membership&apos;s kit. Activates with an open debt; the
                sponsor&apos;s entry commission is deferred until the debt is settled.
              </p>
            )}
          </div>
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <Checkbox
              checked={isCompany}
              onCheckedChange={(checked) => setIsCompany(checked === true)}
              className="mt-0.5"
            />
            <span className="space-y-0.5">
              <span className="text-xs font-medium block">Company slot</span>
              <span className="text-[10px] text-muted-foreground block">
                Exempt from the Unilevel/Stairstep monthly personal-PV maintenance
                requirement — always counts as qualified.
              </span>
            </span>
          </label>
          {defaults?.placement && (
            <p className="text-xs text-muted-foreground">
              Will be placed at the {defaults.placement.position === 'L' ? 'LEFT' : 'RIGHT'} seat
              you clicked.
            </p>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} disabled={!canSubmit || mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create slot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
